import { useState } from 'react';
import { useStore, useTranslate, useResourceContext } from 'ra-core';
import { Box, Button, Popover, useMediaQuery, Tooltip, IconButton } from '@mui/material';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { CustomFieldToggle } from '@/components/global/fields/CustomFieldToggle';
import { Palette } from '@/themes/themeOverrides/Palette';

export const CustomSelectColumnsButton = ({ preferenceKey, className, ...props }) => {
    const resource = useResourceContext(props);
    const finalPreferenceKey = preferenceKey || `${resource}.datagrid`;

    const [anchorEl, setAnchorEl] = useState(null);
    const [availableColumns, setAvailableColumns] = useStore(`preferences.${finalPreferenceKey}.availableColumns`, []);
    const [omit] = useStore(`preferences.${finalPreferenceKey}.omit`, []);
    const [columns, setColumns] = useStore(
        `preferences.${finalPreferenceKey}.columns`,
        availableColumns.filter(column => !omit?.includes(column.source)).map(column => column.index),
    );
    const translate = useTranslate();
    const isXSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const title = translate('ra.action.select_columns', { _: 'Columns' });

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const isOpen = Boolean(anchorEl);

    const handleToggle = event => {
        if (event.target.checked) {
            setColumns(availableColumns.filter(column => column.index === event.target.name || columns.includes(column.index)).map(column => column.index));
        } else {
            setColumns(columns.filter(index => index !== event.target.name));
        }
    };

    const handleMove = (index1, index2) => {
        const index1Pos = availableColumns.findIndex(field => field.index == index1);
        const index2Pos = availableColumns.findIndex(field => field.index == index2);
        if (index1Pos === -1 || index2Pos === -1) {
            return;
        }
        let newAvailableColumns;
        if (index1Pos > index2Pos) {
            newAvailableColumns = [
                ...availableColumns.slice(0, index2Pos),
                availableColumns[index1Pos],
                ...availableColumns.slice(index2Pos, index1Pos),
                ...availableColumns.slice(index1Pos + 1),
            ];
        } else {
            newAvailableColumns = [
                ...availableColumns.slice(0, index1Pos),
                ...availableColumns.slice(index1Pos + 1, index2Pos + 1),
                availableColumns[index1Pos],
                ...availableColumns.slice(index2Pos + 1),
            ];
        }
        setAvailableColumns(newAvailableColumns);
        setColumns(columns => newAvailableColumns.filter(column => columns.includes(column.index)).map(column => column.index));
    };

    // Filter out startIcon from props when rendering IconButton (mobile)
    // eslint-disable-next-line no-unused-vars
    const { startIcon, ...iconButtonProps } = props;

    return (
        <>
            {isXSmall ? (
                <Tooltip title={title}>
                    <IconButton
                        aria-label={title}
                        color='primary'
                        onClick={handleClick}
                        size='large'
                        className={`button--icon-only white ${isOpen ? 'active' : ''} ${className || ''}`}
                        {...iconButtonProps}
                    >
                        <ViewWeekIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <StyledButton
                    size='small'
                    onClick={handleClick}
                    startIcon={<ViewWeekIcon />}
                    className={`button--icon-only white ${isOpen ? 'active' : ''} ${className || ''}`}
                    {...props}
                >
                    {title}
                </StyledButton>
            )}
            <StyledPopover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box className='select-list' component='ul' p={1} my={0}>
                    {availableColumns.map(column => (
                        <CustomFieldToggle
                            className='select-list-item'
                            key={column.index}
                            source={column.source}
                            label={column.label}
                            index={column.index}
                            selected={columns.includes(column.index)}
                            onToggle={handleToggle}
                            onMove={handleMove}
                        />
                    ))}
                </Box>
            </StyledPopover>
        </>
    );
};

const StyledButton = styled(Button, {
    name: 'RaSelectColumnsButton',
    overridesResolver: (props, styles) => styles.root,
})({
    '&.MuiButton-sizeSmall': {
        lineHeight: 1.5,
    },
});

const StyledPopover = styled(Popover)(() => ({
    '& .select-list.MuiBox-root': {
        padding: '20px 12px',
        borderRadius: '20px',
        boxShadow: Palette.boxShadow.elevation12,
        minWidth: '256px',
        '& .MuiSwitch-root.MuiSwitch-sizeSmall': {
            width: '28px',
        },
    },
}));

CustomSelectColumnsButton.propTypes = {
    preferenceKey: PropTypes.string,
    className: PropTypes.string,
    startIcon: PropTypes.node,
};
